import type { Wallet } from "@/types";
import { SupportDetails } from "@/components/ui/copyable-reference";

/**
 * ALL-7239 — the one place a wallet row's explicit balance read state is
 * named.
 *
 * The gateway distinguishes four read outcomes for `balanceStatus`:
 *   ok          the read succeeded (value/zero/freshness are authoritative)
 *   unavailable the chain read failed; a support reference may accompany it
 *   unsupported the provider cannot report a balance for this wallet
 *   not_read    the read was capped and never attempted
 *
 * Only the last three are explicit read failures/non-reads, and only they
 * suppress a stale USD value and the old green freshness claim. `ok` and a
 * row with no status (legacy payloads) keep the existing valuation and zero
 * behaviour exactly as it was.
 *
 * This module deliberately holds the labels and the copy state only. It does
 * not format money, call an API, or add an action: the reference shown is the
 * one the server already sent, copied through the shared CopyableReference
 * adapter so no clipboard implementation is duplicated.
 */

type ExplicitReadStatus = "unavailable" | "unsupported" | "not_read";

const READ_STATE_META: Record<ExplicitReadStatus, { readonly label: string; readonly sourceLabel: string }> = {
  unavailable: { label: "Balance unavailable", sourceLabel: "Read unavailable" },
  unsupported: { label: "Balance read unsupported", sourceLabel: "Unsupported" },
  not_read: { label: "Balance not read", sourceLabel: "Not read" },
};

function isExplicitReadStatus(status: Wallet["balanceStatus"]): status is ExplicitReadStatus {
  return status === "unavailable" || status === "unsupported" || status === "not_read";
}

/**
 * The single descriptor for an explicit read state, or null for `ok` and for
 * a legacy row that carries no `balanceStatus`.
 *
 * WalletRow uses the same descriptor for two decisions so they can never
 * disagree: passing `null` to ValueCell (a status prop alone does not hide a
 * supplied stale number) and replacing the old freshness/“Not priced” text
 * with `sourceLabel`.
 */
export function walletBalanceReadState(
  status: Wallet["balanceStatus"],
): { readonly label: string; readonly sourceLabel: string } | null {
  if (!isExplicitReadStatus(status)) return null;
  return READ_STATE_META[status];
}

/**
 * The read-only notice rendered beneath the balance value.
 *
 * - Renders nothing for `ok`/absent status, so legacy rows are untouched.
 * - The wrapper exposes `data-wallet-balance-read-state` with the actual
 *   backend-mapped status so the Synthetic Customer Lab can count observed
 *   classifications instead of matching marketing copy.
 * - `unavailable` keeps the frozen `wallet-balance-unavailable` test id and
 *   title, with the real reference included when one was supplied.
 * - Only a non-blank reference on an `unavailable` row is rendered for copy;
 *   unsupported/not_read (and recovered `ok`) rows never retain an earlier
 *   failure reference.
 * - The shared reference control is keyed by the exact current reference, so
 *   a changed reference remounts it and never inherits a previous “Copied”
 *   acknowledgement.
 */
export function WalletBalanceReadNotice({
  status,
  supportReference,
}: {
  status: Wallet["balanceStatus"];
  supportReference?: string | null;
}) {
  if (!isExplicitReadStatus(status)) return null;
  const descriptor = READ_STATE_META[status];

  // Only an actual, non-blank reference from an unavailable read is evidence.
  // The supplied value is rendered and copied exactly; whitespace-only text is
  // absent, never a fabricated placeholder.
  const reference =
    status === "unavailable" && typeof supportReference === "string" && supportReference.trim() !== ""
      ? supportReference
      : null;

  return (
    <div data-wallet-balance-read-state={status} className="mt-1 flex flex-col gap-1">
      {status === "unavailable" ? (
        <span
          data-testid="wallet-balance-unavailable"
          className="block text-[11px] text-muted"
          title={
            reference
              ? `Balance unavailable — the chain read failed. Reference ${reference}`
              : "Balance unavailable — the chain read failed"
          }
        >
          {descriptor.label}
        </span>
      ) : (
        <span className="block text-[11px] text-muted">{descriptor.label}</span>
      )}
      {reference && <SupportDetails key={reference} reference={reference} compact />}
    </div>
  );
}
